import { Box, Button, Container, LinearProgress, Stack, Typography } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { Brand } from './Brand';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { regionFromRuntime, regionShortLabel } from '../lib/regionRuntime';

export function StepShell({
  step,
  total,
  title,
  description,
  children,
  onBack,
  onNext,
  nextLabel = 'Continue',
  nextDisabled = false,
  nextLoading = false,
}) {
  const progress = Math.round((step / total) * 100);
  const { state, logout } = useApp();
  const navigate = useNavigate();

  const runtimeRegion = regionFromRuntime(state.company?.region || state.region || 'global');
  const label = regionShortLabel(runtimeRegion);

  const signOut = async () => {
    await logout();
    navigate('/signin', { replace: true });
  };

  return (
    <Box className="app-grid" sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4} gap={2}>
          <Brand />

          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" justifyContent="flex-end">
            <Typography className="mono-label" color="text.secondary">
              Step {step} of {total}
            </Typography>

            <Stack
              direction="row"
              spacing={0.7}
              alignItems="center"
              sx={{
                px: 1.2,
                py: 0.5,
                borderRadius: 999,
                color: 'text.secondary',
                bgcolor: 'rgba(26,29,49,.045)',
                border: '1px solid rgba(26,29,49,.08)',
                cursor: 'default',
                userSelect: 'none',
                whiteSpace: 'nowrap',
              }}
              aria-label={`Current region: ${label}`}
            >
              <PublicRoundedIcon sx={{ fontSize: 15 }} />
              <Typography className="mono-label" variant="caption" color="text.secondary">
                {label}
              </Typography>
            </Stack>

            {state.user && (
              <Button
                size="small"
                color="inherit"
                startIcon={<LogoutRoundedIcon />}
                onClick={signOut}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Sign out
              </Button>
            )}
          </Stack>
        </Stack>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 5,
            borderRadius: 999,
            bgcolor: 'rgba(26,29,49,.08)',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'primary.main',
              borderRadius: 999,
            },
          }}
        />

        <Box className="fade-in" sx={{ pt: { xs: 5, md: 7 }, pb: 4 }}>
          <Typography variant="h2" sx={{ fontSize: { xs: 34, md: 46 }, maxWidth: 680 }}>
            {title}
          </Typography>

          {description && (
            <Typography color="text.secondary" sx={{ mt: 1.5, maxWidth: 650, fontSize: 17 }}>
              {description}
            </Typography>
          )}
        </Box>

        <Box>{children}</Box>

        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={5} pb={4}>
          <Button startIcon={<ArrowBackRoundedIcon />} color="inherit" onClick={onBack} disabled={!onBack}>
            Back
          </Button>

          <Button
            endIcon={<ArrowForwardRoundedIcon />}
            variant="contained"
            onClick={onNext}
            disabled={nextDisabled || nextLoading}
          >
            {nextLoading ? 'Working…' : nextLabel}
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}